import React, { useState, useEffect } from 'react';
import styles from './_performance-reporting.module.scss';
import ArrowDropDown from '@mui/icons-material/ArrowForwardIos';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../Store/rootReducer';
// Removed @mui/lab TabContext/TabList in favor of @mui/material Tabs
import { sort, includes, isEmpty, isNil } from 'ramda';
import {
  loadCompletedPerformanceReportTabs, loadDealerReports, loadAllDealerReports, printBuyHerePayHerePerformanceReport,
  printRetailPerformanceReport, savePerformanceReport, submitPerformanceReport,
  unlockPerformanceReport,
  closePerformanceReport,
  loadPerformanceReport,
  createPerformanceReport,
  rollupPerformanceReport 
} from '../../../Store/PerformanceReport/actions';
import { Button, IconButton, Typography } from '@mui/material';
import TabCompleteIcon from '@mui/icons-material/CheckCircle';
import { ShowModifiedDateWarning, displayMessage, isInRole, sortByYearThenMonthDescending, userCanSwitchClients, userHasPermission } from '../../../Helpers/Utilities';
import { useDialog } from "../../Providers/DialogProvider";
import { BusinessTypes, DpgReportStatuses, NiadaAdministrator, permissions } from '../../../Helpers/Constants';
import { PermissionSet } from '../../../Models/User';
import DownloadIcon from '@mui/icons-material/Download';
import ConfirmationDialog from '../../Dialogs/ConfirmationDialog/ConfirmationDialog';
import CategorySwitcherDrawer from '../../Controls/CategorySwitcherDrawer/CategorySwitcherDrawer';
import { IPerformanceCategory } from '../../../Models/PerformanceCategory';
import TabNotCompleteIcon from '@mui/icons-material/PanoramaFishEye'
import { authService } from '../../..';
import DealerReportsGrid from './DealerReportsGrid/DealerReportsGrid';
import { DealerReport } from '../../../Models/DealerReport';
import PeformanceCategoryTabPanel from './PerformanceCategoryTabPanel/PerformanceCategoryTabPanel';
import PublishIcon from '@mui/icons-material/Publish';
import CreatePerformanceReportDialog from './CreatePerformanceReportDialog/CreatePerformanceReportDialog';
import KeyIcon from '@mui/icons-material/Key';
import DynamicButton from '../../Controls/DynamicButton/DynamicButton';
import { useParams } from 'react-router-dom';
import LoadingPanel from '../../Controls/LoadingPanel/LoadingPanel';
import { SvgIcon } from '@progress/kendo-react-common';
import * as svgIcons from '@progress/kendo-svg-icons';
import CompositeReport from '../../Controls/CompositeReport/CompositeReport';
import { BusinessType } from '../../../Models/BusinessType';

export const TabStatusIndicator = (props: { categoryId: string, completedCategoryList: string[] }): React.ReactElement => {
  return (
    <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '2px', paddingTop: '0px', paddingBottom: '0px', paddingRight: '0px' }}>
      {includes(props.categoryId, props.completedCategoryList) && (
        <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
          <div style={{ color: 'green', width: '1px', display: 'flex', justifyContent: 'flex-end', marginRight: '0px' }}></div><TabCompleteIcon style={{ color: 'green' }} />
        </div>
      )}
      {!includes(props.categoryId, props.completedCategoryList) && (
        <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
          <div style={{ color: 'green', width: '1px', display: 'flex', justifyContent: 'flex-end', marginRight: '0px' }}></div><TabNotCompleteIcon style={{ color: 'black' }} />
        </div>
      )}
    </td>
  );
};

export default function PerformanceReporting() {
  const [tabIndex, setTabIndex] = useState<string>("0");
  const [currentTabCategoryId, setCurrentTabCategoryId] = useState<string>("8ad1571c-4f7a-4791-ac3a-611231760a2c"); // default to first tab value
  const [isCategorySwitcherDrawerOpen, setIsCategorySwitcherDrawerOpen] = React.useState<boolean>(false);

  // --- Rollup UX state machine ---
  type RollupPhase = 'idle' | 'rolling' | 'reloading';
  const [rollupLoading, setRollupLoading] = useState<boolean>(false);
  const [rollupPhase, setRollupPhase] = useState<RollupPhase>('idle');

  const dealers = useSelector((state: AppState) => state.library.dpgClients);
  const clientBusinessTypes = useSelector((state: AppState) => state.library.clientBusinessTypes);
  const performanceItemCategories = useSelector((state: AppState) => state.library.performanceItemCategories);
  const completedCategories = useSelector((state: AppState) => state.performanceReport.completedCategories);
  const performanceItems = useSelector((state: AppState) => state.library.performanceItems);
  const performanceReport = useSelector((state: AppState) => state.performanceReport.performanceReport);
  const [dealerReportEditorOpen, setDealerReportEditorOpen] = useState<boolean>(false);
  const loading = useSelector((state: AppState) => state.performanceReport.loadingPerformanceReport);
  const saving = useSelector((state: AppState) => state.performanceReport.savingPerformanceReport);
  const currentUser = useSelector((state: AppState) => state.shared.currentUser);
  const selectedClientId = useSelector((state: AppState) => state.performanceReport.selectedClientId);
  const dealerReports = useSelector((state: AppState) => state.performanceReport.dealerReports?.sort(sortByYearThenMonthDescending) || []);
  const dealerReportsPageLoading = useSelector((state: AppState) => state.shared.dealerReportsPageLoading);
  const updateReportDetails = useSelector((state: AppState) => state.shared.updateReportDetails);
  const [useIconButtons, setUseIconButtons] = useState<boolean>(true);

  const dispatch = useDispatch();
  const [openDialog, closeDialog] = useDialog();

  const [bearerToken, setBearerToken] = useState<string>('');
  useEffect(() => {
    const setToken = async () => {
      const token = await authService.getToken();
      setBearerToken(token);
    }
    setToken().catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedClientId) {
      dispatch(loadDealerReports(selectedClientId));
    } else if (isInRole(currentUser?.employeeRoleIds ?? [], NiadaAdministrator) || currentUser?.isSuperUser) {
      dispatch(loadAllDealerReports());
    }
  }, [selectedClientId, currentUser]);

  const currentTabComplete = includes(currentTabCategoryId, completedCategories);
  const reportSubmitted = performanceReport?.statusId === DpgReportStatuses.submitted;

  const canChangeClients: boolean = currentUser !== null && (userHasPermission(currentUser, permissions.clients, PermissionSet.U) || currentUser.isSuperUser);
  const reportPerformanceItemIds = performanceReport?.performanceReportItemValues?.map(pi => pi.performanceItemId);
  const filteredPerformanceItemCategoryIds = performanceItems.filter(pi => includes(pi.id, reportPerformanceItemIds ?? [])).map(pi => pi.performanceItemCategoryId);
  const sortedCategories = sort((left: IPerformanceCategory, right: IPerformanceCategory) => left.sortOrder < right.sortOrder ? -1 : 1,
    performanceItemCategories.filter(pic => includes(pic.id, filteredPerformanceItemCategoryIds)));

  const params = useParams<{ id: string }>();
  useEffect(() => {
    if (params.id) {
      showReportInput(params.id);
    }
  }, [params]);

  const showReportInput = (id: string) => {
    dispatch(loadPerformanceReport(id));
  }
  useEffect(() => {
    if (performanceReport) {
      dispatch(loadCompletedPerformanceReportTabs(performanceReport.id!));
    }
  }, [performanceReport]);

  const viewCompositeReport = (month: number, year: number, performanceGroup: string, reportTypeId: string) => {
    openDialog({
      fullScreen: true,
      children: (
        <CompositeReport 
          bearerToken={bearerToken}
          year={year}
          month={month}
          reportTypeId={reportTypeId}
          performanceGroup={performanceGroup}
          closeDialog={closeDialog}
          baseUrl={authService.appSettings.GetWebApiBaseUri()} 
        />
      ),
    });
  }

  const handleRollupClick = () => {
  if (performanceReport?.id) {
    setRollupLoading(true);
    setRollupPhase('rolling');

    const result: any = dispatch(rollupPerformanceReport(performanceReport.id));

    if (result && typeof result.then === 'function') {
      result
        .then((res: any) => {
          if (res?.Error) {
            // Show API error instead of success
            dispatch(displayMessage(res.Error, "error"));
            setRollupLoading(false);
            setRollupPhase('idle');
          } else {
            // Success path
            dispatch(displayMessage("Child performance data has been rolled up into this report.", "success"));
          }
        })
        .catch(() => {
          setRollupLoading(false);
          setRollupPhase('idle');
        });
    }
  }
};


  // --- Phase transitions tied to Redux loading flag ---
  // As soon as the report starts reloading after rollup, switch to 'reloading' phase.
  useEffect(() => {
    if (rollupLoading) {
      if (rollupPhase === 'rolling' && loading) {
        setRollupPhase('reloading');
      }
      // When we've seen loading turn off after being on, the reload has completed → hide panel
      if (rollupPhase === 'reloading' && !loading) {
        setRollupLoading(false);
        setRollupPhase('idle');
      }
    }
  }, [loading, rollupLoading, rollupPhase]);

  const selectedDealer = dealers.find(d => d.id === selectedClientId);
  const dealerName = dealers.find(d => d.id === performanceReport?.clientId)?.name ?? '';
  return (
    dealerReportsPageLoading !== true ? <>
      {currentUser && (currentUser.clientId || canChangeClients) && (
        <div className={styles.performanceReportingContainer}>
          {performanceReport && !loading && (
            <div className={styles.performanceReport}>
              <div className={styles.performanceReportTitleRow}>
                <div className={styles.title}>
                  <IconButton
                    onClick={() => setIsCategorySwitcherDrawerOpen(true)}
                  >
                    <ArrowDropDown style={{ color: '#d95137' }} />
                  </IconButton>
                  <CategorySwitcherDrawer
                    isOpen={isCategorySwitcherDrawerOpen}
                    categories={sortedCategories}
                    completedCategories={completedCategories}
                    onSelectCategory={(category: IPerformanceCategory) => {
                      setCurrentTabCategoryId(sortedCategories.find(c => c.description === category.description)!.id!);
                      setTabIndex(sortedCategories.findIndex(c => c.description === category.description).toString());
                      setIsCategorySwitcherDrawerOpen(false)
                    }}
                    onClose={() => setIsCategorySwitcherDrawerOpen(false)}
                  />
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ marginLeft: '10px' }}>{dealerName}</div>
                    <div style={{ marginLeft: '10px' }}>
                      &#x2022; {performanceReport.month && performanceReport.year 
                        ? `${performanceReport.month.toString().padStart(2, '0')}/${performanceReport.year}` 
                        : 'Loading...'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', background: useIconButtons ? 'inherit' : 'inherit' }}>
                  {reportSubmitted && (
                    <>
                      <DynamicButton
                        title={'View Composite Report'}
                        useIconButton={useIconButtons}
                        style={useIconButtons
                          ? { marginRight: '10px' }
                          : { marginRight: '10px', height: '30px', fontSize: '14px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', background: '#e36222', overflow: 'hidden', width: '220px' }
                        }
                        titleStyle={{ color: 'white' }}
                        buttonContent={<SvgIcon icon={svgIcons['fileReportIcon']}  style={{color: 'green', cursor: 'pointer', padding: '0px', width: 'calc(14.5px * 2)', height: 'calc(14.5px * 2)'}} />}
                        color="secondary"
                        fillMode='solid'
                        disabled={isEmpty(bearerToken)}
                        onClick={() => {
                          viewCompositeReport(performanceReport.month, performanceReport.year, performanceReport.performanceGroup, performanceReport.businessTypeId);
                        }}
                      />
                      <DynamicButton
                        title={useIconButtons ? 'Unlock the report for editing.' : 'Unlock Form'}
                        useIconButton={useIconButtons}
                        color='white'
                        titleStyle={{ color: 'white' }}
                        style={useIconButtons ? { outline: 'none', marginRight: '10px' } : { backgroundColor: 'rgb(240, 179, 12)', marginRight: '10px' }}
                        onClick={() => {
                          dispatch(unlockPerformanceReport(performanceReport));
                        }}
                        buttonContent={useIconButtons ? <div style={{ position: 'relative' }}>
                          <span className="k-icon k-font-icon k-i-lock" style={{ fontSize: '28px', color: '#696969' }} />
                          <KeyIcon className={styles.rotateKey} style={{ fontSize: '14px', position: 'absolute', left: '8px', top: '14px', color: 'white', }} />
                        </div> : undefined}
                      />
                    </>
                  )}

                  {selectedDealer?.hasChildClients && (
                    <DynamicButton
                      title={'Rollup Current Month'}
                      useIconButton={useIconButtons}
                      style={useIconButtons
                        ? { marginRight: '10px' }
                        : { marginRight: '10px', height: '30px', fontSize: '14px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', width: '240px', background: '#0c5927' }
                      }
                      titleStyle={{ color: 'white' }}
                      color="white"
                      disabled={rollupLoading}
                      onClick={handleRollupClick}
                      buttonContent={
                        <span className={`${styles.rollupButtonText} ${useIconButtons ? styles.iconMode : styles.textMode}`}>
                          Rollup Current Month
                        </span>
                      }
                    />
                  )}

                  <DynamicButton
                    title={useIconButtons ? 'Download the current report input data as PDF.' : 'Current Form'}
                    style={useIconButtons ? { marginRight: '10px', outline: 'none' } : { backgroundColor: '#747373', marginRight: '10px', height: '30px' }}
                    titleStyle={{ color: 'white' }}
                    onClick={() => {
                      if (performanceReport.businessTypeId?.toLowerCase() === BusinessTypes.retail) {
                        dispatch(printRetailPerformanceReport(performanceReport.month, performanceReport.year, performanceReport.clientId));
                      } else {
                        dispatch(printBuyHerePayHerePerformanceReport(performanceReport.month, performanceReport.year, performanceReport.clientId));
                      }
                    }}
                    useIconButton={useIconButtons}
                    color={'white'}
                    buttonContent={<DownloadIcon style={useIconButtons ? { fontSize: '30px' } : { fontSize: '25px', color: 'white' }} />}
                  />

                  {!reportSubmitted && (
                    <>
                      {(sortedCategories.length === completedCategories.length) && (
                        <IconButton
                          title="Submit report for review."
                          className={styles.denseButton}
                          style={{ marginRight: '10px' }}
                          onClick={async () => {
                            if (await ShowModifiedDateWarning(performanceReport)) {
                              openDialog({
                                fullScreen: false,
                                children: (
                                  <ConfirmationDialog
                                    closeDialog={closeDialog}
                                    prompt="The application has been saved by someone else since you opened it. Continue submitting anyway?"
                                    title="Submit and Overwrite?"
                                    accept={() => {
                                      closeDialog();
                                      dispatch(submitPerformanceReport(performanceReport));
                                      dispatch(displayMessage("The composite will be regenerated. This will take a couple of minutes.", "success"));
                                    }}
                                  />
                                ),
                              });
                            } else {
                              dispatch(submitPerformanceReport(performanceReport));
                              dispatch(displayMessage("The composite will be regenerated. This will take a couple of minutes.", "success"));
                            }
                          }}
                        >
                          <PublishIcon fontSize='large' style={{ color: '#0970bb' }} />
                        </IconButton>
                      )}
                    </>
                  )}

                  <IconButton
                    title="Reload report inputs and revert any unsaved changes."
                    className={styles.submitButton}
                    style={{ outline: 'none', marginRight: '10px' }}
                    onClick={() => {
                      openDialog({
                        fullScreen: false,
                        children: (
                          <ConfirmationDialog
                            closeDialog={closeDialog}
                            prompt="Are you sure you want to reload the report and lose any unsaved changes?"
                            title="Revert all changes?"
                            accept={() => {
                              closeDialog();
                              dispatch(loadPerformanceReport(performanceReport.id!));
                            }}
                          />
                        ),
                      });
                    }}
                  >
                    <span style={{ color: '#f0b30c', fontSize: '25px' }} className="k-icon k-font-icon k-i-reset"></span>
                  </IconButton>
                  <IconButton
                    title={reportSubmitted ? 'Unlock Report to update and save.' : 'Save Report Input'}
                    className={styles.denseButton}
                    style={{ outline: 'none', marginRight: '10px' }}
                    disabled={reportSubmitted}
                    onClick={async () => {
                      if (await ShowModifiedDateWarning(performanceReport)) {
                        openDialog({
                          fullScreen: false,
                          children: (
                            <ConfirmationDialog
                              closeDialog={closeDialog}
                              prompt="The application has been saved by someone else since you opened it. Continue saving anyway?"
                              title="Save and Overwrite?"
                              accept={() => {
                                closeDialog();
                                dispatch(savePerformanceReport(performanceReport));
                              }}
                            />
                          ),
                        });
                      } else {
                        dispatch(savePerformanceReport(performanceReport));
                      }
                    }}
                  >
                    <span style={{ color: reportSubmitted ? '#bfbfbf' : '#0c5927', fontSize: '25px' }} className="k-icon k-font-icon k-i-save" />
                  </IconButton>

                  <IconButton
                    title="Close Report Input"
                    className={styles.submitButton}
                    style={{ borderRadius: '0px', outline: 'none' }}
                    onClick={() => {
                      openDialog({
                        fullScreen: false,
                        children: (
                          <ConfirmationDialog
                            closeDialog={closeDialog}
                            prompt="Are you sure you want to close the report and lose any unsaved changes?"
                            title="Close Report Input?"
                            accept={() => {
                              closeDialog();
                              dispatch(closePerformanceReport());
                            }}
                          />
                        ),
                      });
                    }}
                  >
                    <span style={{ fontSize: '25px', color: '#d10000', marginRight: '10px' }} className="k-icon k-font-icon k-i-close" />
                  </IconButton>
                </div>
              </div>
              <Tabs
                value={tabIndex}
                onChange={(event: React.SyntheticEvent, newValue: string) => {
                  setTabIndex(newValue?.toString());
                  setCurrentTabCategoryId(sortedCategories[parseInt(newValue?.toString())]?.id!);
                }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="scrollable force tabs example"
              >
                  {sortedCategories.map((c: IPerformanceCategory, key: number) => (
                    <Tab
                      label={c.description}
                      key={key}
                      value={key.toString()}
                      iconPosition="end"
                      icon={<TabStatusIndicator
                        categoryId={c.id ?? ''}
                        completedCategoryList={completedCategories}
                      />} />))
                  }
              </Tabs>
                {sortedCategories
                  .map((performanceCategory: IPerformanceCategory, key: number) => (
                    <PeformanceCategoryTabPanel
                      key={key}
                      performanceReport={performanceReport}
                      performanceCategory={performanceCategory}
                      tabValue={key.toString()}
                      activeTabValue={tabIndex}
                      tabIsComplete={currentTabComplete}
                      reportSubmitted={reportSubmitted}
                      completedCategories={completedCategories}
                      performanceItems={performanceItems}
                      tabCategoryId={performanceCategory.id!}
                    />
                  ))
                }

            </div>
          )}
          {!performanceReport && (selectedClientId || userCanSwitchClients(currentUser)) && dealerReports && !saving && (
            <DealerReportsGrid
              canCreateReportInputData={!isNil(selectedClientId)}
              dealerReports={dealerReports}
              onOpenReportInputDataEntry={(dealerReport: DealerReport) => {
                showReportInput(dealerReport.id);
              }}
              onViewComposite={(dealerReport: DealerReport) => {
                viewCompositeReport(dealerReport.month, dealerReport.year, dealerReport.dealerGroup, dealerReport.businessTypeId);
              }}
              onCreateItem={() => {
                setDealerReportEditorOpen(true);
              }}
            />
          )}
          {(loading || saving || rollupLoading) && (
            <LoadingPanel 
              style={{ position: 'absolute', top: '0px', left: '0px', width: '100vw', height: '100vh' }} 
              label={rollupLoading ? 'Rolling up child performance data...' : 'Loading report...'}
            />
          )}
        </div>
      )}
      {currentUser && !currentUser.clientId && !canChangeClients && (
        <div style={{ background: '#9a9aa4', padding: '20px' }}>
          <p>
            <Typography variant="h6" style={{ fontWeight: 'bold', color: '#6a0204' }}>You are a member or guest member of AMS Analytics, but you are not setup as a Dealer Group User.</Typography>
          </p>
          <p>
            <Typography variant="h6" style={{ color: '#6a0204' }}>If you have multiple logins, try logging out of this account by using the account menu at the top right. Then login with your NIADA account.</Typography>
          </p>
          <p>
            <div style={{ display: 'flex', flex: 1, alignItems: 'center' }}><Typography variant="h6" style={{ color: '#6a0204', display: 'flex', alignItems: 'center' }}><span>If you believe this is an error, you can contact support by clicking the ? icon in the top right corner. Provide a screen shot and any other information you think will help in resolving this issue.</span></Typography></div>
          </p>
        </div>
      )}
      {!currentUser && (
        <div style={{ background: '#9a9aa4', padding: '20px' }}>
          <p>
            <Typography variant="h6" style={{ fontWeight: 'bold', color: '#6a0204' }}>No user record was found.</Typography>
          </p>
          <p>
            <Typography variant="h6" style={{ color: '#6a0204' }}>If you believe this is an error, you can contact support by clicking the ? icon in the top right corner. Provide a screen shot and any other information you think will help in resolving this issue.</Typography>
          </p>
        </div>
      )}
      {dealerReportEditorOpen && (
        <CreatePerformanceReportDialog
          dealerReports={dealerReports}
          businessTypes={clientBusinessTypes?.map(b => ({ id: b.id, name: b.name } as BusinessType)) || []}
          hasChildClients={selectedDealer?.hasChildClients || false}
          onCancel={() => setDealerReportEditorOpen(false)}
          onSubmit={(reportingPeriod: { month: number; year: number, businessTypeId: string, captureDataFromChildren?: boolean }) => {            
            dispatch(createPerformanceReport(reportingPeriod.month, reportingPeriod.year, reportingPeriod.businessTypeId, selectedClientId, reportingPeriod.captureDataFromChildren));
            setDealerReportEditorOpen(false);
          }}
        />
      )}
      {updateReportDetails && (
        <LoadingPanel label={"Updating Report..."} style={{ position: 'absolute', width: '100%', textAlign: 'center' }} />
      )}
    </> : <LoadingPanel label={"Loading Dealer Reports..."} />
  );
}
